import { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "RESET") return { scope: action.scope, items: [] };
  if (action.scope !== state.scope) return state;
  if (action.type === "LOAD_WHATSAPPS") return { ...state, items: action.payload };
  if (action.type === "UPDATE_WHATSAPPS") {
    const exists = state.items.some(item => item.id === action.payload.id);
    return { ...state, items: exists ? state.items.map(item => item.id === action.payload.id ? { ...item, ...action.payload } : item) : [action.payload, ...state.items] };
  }
  if (action.type === "UPDATE_SESSION") {
    const changes = Object.fromEntries(["status", "updatedAt", "qrcode", "retries"].filter(key => action.payload[key] !== undefined).map(key => [key, action.payload[key]]));
    return { ...state, items: state.items.map(item => item.id === action.payload.id ? { ...item, ...changes } : item) };
  }
  if (action.type === "DELETE_WHATSAPPS") return { ...state, items: state.items.filter(item => item.id !== action.payload) };
  return state;
};

const useWhatsApps = () => {
  const { user, socket, isAuth, loading: authLoading } = useContext(AuthContext);
  const scope = isAuth && !authLoading && user?.id && user?.companyId ? `${user.companyId}:${user.id}` : null;
  const companyId = user?.companyId;
  const [state, dispatch] = useReducer(reducer, { scope: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const activeScope = useRef(null);
  const requestNumber = useRef(0);

  const reload = useCallback(async () => {
    if (!scope || activeScope.current !== scope) return;
    const request = ++requestNumber.current;
    const current = () => activeScope.current === scope && requestNumber.current === request;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/whatsapp/?session=0");
      if (!Array.isArray(data)) throw new Error("Resposta inválida ao carregar conexões");
      if (current()) dispatch({ type: "LOAD_WHATSAPPS", payload: data, scope });
    } catch (failure) {
      if (current()) setError(failure);
    } finally {
      if (current()) setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    activeScope.current = scope;
    dispatch({ type: "RESET", scope });
    setError(null);
    if (scope) reload();
    else setLoading(!!authLoading);
    return () => { activeScope.current = null; requestNumber.current += 1; };
  }, [scope, authLoading, reload]);

  useEffect(() => {
    if (!scope || !socket?.on || !socket?.off) return;
    const onWhatsapp = (data) => {
      if (data.action === "update") dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp, scope });
      if (data.action === "delete") dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId, scope });
    };
    const onSession = (data) => {
      if (data.action === "update") dispatch({ type: "UPDATE_SESSION", payload: data.session, scope });
    };
    const whatsappEvent = `company-${companyId}-whatsapp`;
    const sessionEvent = `company-${companyId}-whatsappSession`;
    socket.on(whatsappEvent, onWhatsapp);
    socket.on(sessionEvent, onSession);
    socket.on("connect", reload);
    return () => {
      socket.off(whatsappEvent, onWhatsapp);
      socket.off(sessionEvent, onSession);
      socket.off("connect", reload);
    };
  }, [socket, companyId, scope, reload]);

  return { whatsApps: state.scope === scope ? state.items : [], loading: !!authLoading || (!!scope && state.scope !== scope) || loading, error, reload };
};

export default useWhatsApps;
